import get from 'lodash/get';
import reduce from 'lodash/reduce';
import isUndefined from 'lodash/isUndefined';
import type { FormSubmitterState } from './form-submitter-type';
import { TYPE_UPLOAD_FIELD, FORM_TYPE_FILE_REQUEST } from '../../../constants';

const getDefaultSelectedValues = () => [];
const defaultTextValue = '';
const getDefaultItemsArray = () => [];
const getDefaultItemsInUploadQueueArray = () => [];
const defaultDateValue = '';
const defaultError = null;

export const formSubmitterSelectors = {
  getTextValueForId: (state: FormSubmitterState, id: string) => {
    const value = get(state, `response.${id}`, defaultTextValue);
    return value;
  },
  getDateValueForId: (state: FormSubmitterState, id: string) => {
    const value = get(state, `response.${id}`, defaultDateValue);
    return value;
  },
  getSelectedValuesForId: (state: FormSubmitterState, id: string) => {
    const values = get(state, `response.${id}`, getDefaultSelectedValues());
    return values;
  },
  getErrorForId: (state: FormSubmitterState, id: string) => {
    // no error should be shown if the component is disabled
    const disabled = get(state, `components.${id}.disabled`, false);
    if (disabled) {
      return null;
    }
    const error = get(state, `errors.${id}`, defaultError);
    return error;
  },
  getItemsForId: (state: FormSubmitterState, id: string) => {
    const items = get(state, `itemQueues.${id}.items`, getDefaultItemsArray());
    return items;
  },
  getComponentForId: (state: FormSubmitterState, id: string) => {
    const component = get(state, `components.${id}`);
    return component;
  },
  countAllItemsInItemQueues: (state: FormSubmitterState) => {
    return reduce(
      state.itemQueues,
      (acc, queue) => {
        return acc + queue.items.length;
      },
      0,
    );
  },
  getAllItemsInItemQueues: (state: FormSubmitterState) => {
    return reduce(
      state.itemQueues,
      // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2769
      (acc, queue) => {
        return [...acc, ...queue.items];
      },
      [],
    );
  },
  getAllItemsInUploadQueue: (state: FormSubmitterState) => {
    return get(
      state,
      `uploader.itemsInUploadQueue`,
      getDefaultItemsInUploadQueueArray(),
    );
  },
  getPageLayout: (state: FormSubmitterState) => {
    return get(state, `form.content.layouts.group-0.layout`, undefined);
  },
  getMetadataFieldsForUploadField: (
    state: FormSubmitterState,
    uploadFieldId: string,
  ): Array<string> => {
    /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
    return Object.keys(state.components).reduce<Array<any>>(
      (fieldIds, componentId) => {
        // Skip if the component is not a metadata field
        if (!state.components[componentId]?.metadataFieldKey) {
          return fieldIds;
        }

        const metadataHasUpload =
          Array.isArray(state.components[componentId]?.uploadFieldIds) &&
          state.components[componentId]?.uploadFieldIds.includes(uploadFieldId);

        const isOldFileRequest =
          state.form.formType === FORM_TYPE_FILE_REQUEST &&
          isUndefined(state.components[componentId].uploadFieldIds);

        if (metadataHasUpload || isOldFileRequest) {
          // The first condition explicitly checks if the component has uploadFieldIds array
          // All forms metadata fields should have this array
          // In file request, this will only exist on recently saved builder forms

          // The second condition is to handle older file request builder forms
          // Since uploadFieldIds is a new field, it does not exist on older file request metadata fields
          // If it is undefined for the metadata field, we assume it should be linked to the upload field
          fieldIds.push(componentId);
        }

        return fieldIds;
      },
      [],
    );
  },
  getAllMetadataFieldsIds: (state: FormSubmitterState): Array<string> => {
    /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
    return Object.keys(state.components).reduce<Array<any>>(
      (fieldIds, componentId) => {
        if (state.components[componentId]?.metadataFieldKey) {
          fieldIds.push(componentId);
        }

        return fieldIds;
      },
      [],
    );
  },
  getFolderIdForUploadField: (
    state: FormSubmitterState,
    uploadFieldId: string,
  ) => {
    if (state.components[uploadFieldId]?.type !== TYPE_UPLOAD_FIELD) {
      return undefined;
    }
    return get(state, `components.${uploadFieldId}.folderId`, undefined);
  },
  getTosChecked: (state: FormSubmitterState) => {
    // TosChecked can be undefined, true, or false
    return state.TosChecked;
  },
} as const;

export type FormSubmitterSelectors = typeof formSubmitterSelectors;
